import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { Grid } from 'react-bootstrap';
import { get } from 'lodash';
import moment from 'moment';
import fastMemoize from 'fast-memoize';
import { LoadingTile } from 'components/molecules/ErrorTile';
import { fetchList } from 'services/offers/duck';
import DebounceInputChange from 'hoc/DebounceInputChange';
import StyledReactTable from 'components/molecules/StyledReactTable';
import Pagination from 'components/molecules/tables/components/Pagination';
import CellDiv from 'components/molecules/tables/components/CellDiv';

const NOT_AVAILABLE = 'N/A';
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 25;

const DebouncedInput = DebounceInputChange(500)(props => <input {...props} />);

const generateFilterValue = (filter, row) => row[filter.id];
const memoizeFilterValue = fastMemoize(generateFilterValue);

class OffersTableContainer extends Component {
    constructor(props) {
        super(props);
        this.getColumns = this.getColumns.bind(this);

        this.state = {
            showPagination: false,

            currentUrl: props.parsedUrl.href,
            list: null,

            // controlled table
            // filtered: [],
            // sorted: [],
            // page: 0,
            // pageSize: 25
            ...OffersTableContainer.convertUriToState(props.parsedUrl)
        };

        this.offersTableInstance = React.createRef();
    }

    dateFormatter(props) {
        return `${moment(props.value).format('MM-DD-YYYY')}`;
    }

    brandLink(props) {
        return (
            <Link title="View Brand" to={`/admin/brands/${props.original.brandId}`}>
                {props.value}
            </Link>
        );
    }

    getOffersTableInstance() {
        if (!this.offersTableInstance) {
            console.warn('React Table - No instance'); // eslint-disable-line
        }

        return this.offersTableInstance;
    }

    static notAvailableValueFilter(cell) {
        if (!cell) {
            return NOT_AVAILABLE;
        }

        return cell;
    }

    getColumns() {
        return [
            {
                Header: 'TITLE',
                accessor: 'title',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control title-filter"
                        onChange={value => onChange(value)}
                        placeholder="Title"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);

                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            }, {
                Header: 'BRAND',
                // Cell: props => this.nameLink(null, props.original),
                accessor: 'brandName',
                Cell: props => this.brandLink(props),
            }, {
                Header: 'CREATED',
                accessor: 'createdAt',
                Cell: props => <CellDiv value={this.dateFormatter(props.value)} type="date" style={{ textAlign: 'right' }} />,
                maxWidth: 150
            }
        ];
    }

    componentDidMount() {
        this.props.fetchList();
    }

    static getDerivedStateFromProps(props, state) {
        const propList = get(props, 'offers.list', []);
        const propUrl = get(props, 'parsedUrl', {});
        const mergeList = [];

        if (propList !== state.list) {
            let showPagination = state.showPagination;

            if (!state.showPagination && propList.length > DEFAULT_PAGE_SIZE) {
                showPagination = true;
            }

            mergeList.push({
                list: propList,
                showPagination
            });
        }

        if (state.currentUrl !== propUrl.href) {
            mergeList.push({
                currentUrl: propUrl.href,
                ...OffersTableContainer.convertUriToState(propUrl)
            });
        }

        if (mergeList.length) {
            return Object.assign.apply(null, [
                {},
                state
            ].concat(mergeList));
        }
        return null;
    }

    onFilteredChangeCustom(value, accessor) {
        let filtered = this.state.filtered;

        const tempFilters = filtered.reduce((prev, filter) => {
            prev[filter.id] = filter.value;
            return prev;
        }, {});

        tempFilters[accessor] = value;

        filtered = Object.keys(tempFilters).map((key) => {
            if (tempFilters[key] === null) {
                return false;
            }
            return { id: key, value: tempFilters[key] };
        }).filter(item => item);

        const instance = this.getOffersTableInstance().current;
        let page = this.state.page;
        if (get(instance, 'state.sortedData', []).length < this.state.page * this.state.pageSize) {
            page = DEFAULT_PAGE;
        }

        this.setState({ filtered, page }, () => this.mirrorStateToUri());
    }

    mirrorStateToUri() {
        const query = {};

        this.state.filtered.forEach((filter) => {
            switch (filter.id) {
            case 'title':
                if (filter.value) {
                    query.title = filter.value;
                }
                break;

            default:
                // ignore
            }
        });

        if (this.state.page !== DEFAULT_PAGE) {
            query.page = this.state.page;
        }

        if (this.state.pageSize !== DEFAULT_PAGE_SIZE) {
            query.pageSize = this.state.pageSize;
        }

        if (this.state.sorted.length) {
            const sortedItem = this.state.sorted[0];

            query.sort = sortedItem.id;
            query.sortDir = sortedItem.desc ? 'desc' : 'asc';
        }

        this.props.parsedUrl.set('query', query);
        this.props.push(this.props.parsedUrl.toString({ onlyPath: true }));
    }

    static convertUriToState(url) {
        const filtered = [];
        const sorted = [];
        const { query } = url;

        if (query.title) {
            filtered.push({ id: 'title', value: query.title });
        }

        if (query.sort) {
            sorted.push({ id: query.sort, desc: query.sortDir === 'desc' });
        }

        let page = Number(query.page);
        if (!page || page < 0) {
            page = DEFAULT_PAGE;
        }

        let pageSize = Number(query.pageSize);
        if (!pageSize || pageSize < 1) {
            pageSize = DEFAULT_PAGE_SIZE;
        }

        return {
            filtered,
            sorted,
            page,
            pageSize
        };
    }

    render() {
        const data = this.props.offers.list;
        const dataFetched = this.props.offers.listFetched;

        return (
            <Grid fluid>
                {dataFetched ? (
                    <StyledReactTable
                        data={data}
                        ref={this.userTableInstance}
                        defaultPageSize={25}
                        minRows={10}
                        filtered={this.state.filtered}
                        sorted={this.state.sorted}
                        page={this.state.page}
                        pageSize={this.state.pageSize}
                        onFilteredChange={(filtered, column, value) => {
                            this.onFilteredChangeCustom(value, column.id || column.accessor);
                        }}
                        onSortedChange={sorted => this.setState({ sorted }, () => this.mirrorStateToUri())}
                        onPageChange={page => this.setState({ page }, () => this.mirrorStateToUri())}
                        onPageSizeChange={(pageSize, page) => this.setState({
                            page, pageSize
                        }, () => this.mirrorStateToUri())}
                        getTheadThProps={() => ({
                            style: {
                                backgroundColor: '#E2E2E2',
                            }
                        })}
                        getTheadFilterThProps={() => ({
                            style: {
                                overflow: 'inherit' // allows react-select to work
                            }
                        })}
                        columns={this.getColumns()}
                        defaultSorted={[{
                            id: 'fullName', desc: false
                        }]}
                        className="-highlight"
                        showPagination={this.state.showPagination}
                        PaginationComponent={Pagination}
                    />
                ) : (
                    <LoadingTile />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    offers: state.offers,
    parsedUrl: state.router.parsedUrl
});

export default connect(mapStateToProps, { fetchList, push })(OffersTableContainer);



// WEBPACK FOOTER //
// ./src/containers/tables/OffersTableContainer.js